<template>
  <div class="okr-form-new">
    <div class="okr-form-header mb-1">
      <div class="d-flex align-items-start justify-content-between">
        <div>
          <div class="font-weight-bolder">
            {{ hasExisting ? "Editando OKR" : "Registrar OKR" }} — {{ quarterLabel }}
          </div>
          <div class="text-muted font-small-2">{{ quarterMonthsLabel }}</div>
        </div>
        <b-button variant="outline-secondary" size="sm" @click="$emit('close')">
          <feather-icon icon="ArrowLeftIcon" size="14" class="mr-25" />
          Voltar
        </b-button>
      </div>
    </div>

    <b-card v-if="loading" class="text-center py-3">
      <b-spinner variant="secondary" />
    </b-card>

    <b-card v-else-if="loadError" class="text-center py-3">
      <feather-icon icon="AlertCircleIcon" size="24" class="text-danger mb-75" />
      <p class="text-danger mb-50">Não foi possível carregar o formulário.</p>
      <b-button size="sm" variant="outline-secondary" @click="init">Tentar novamente</b-button>
    </b-card>

    <template v-else>
      <b-alert v-if="isLegacyOkr" show variant="warning" class="font-small-3 py-50 px-1">
        Este OKR foi registrado no modelo antigo, por isso os campos iniciam vazios.
        Ao salvar, ele será convertido para o modelo novo.
      </b-alert>

      <!-- ── Card 1: Premissas ─────────────────────────────────── -->
      <b-card>
        <OkrPremisesBlock
          :consultant-id="consultantId"
          :year="year"
          :quarter="quarter"
          @premises-updated="applyPremises"
        />
      </b-card>

      <!-- ── Card 2: Editar OKR ────────────────────────────────── -->
      <b-card>
        <div class="card-block-title mb-1">Editar OKR</div>
        <b-row>
          <b-col cols="12" lg="6">
          <!-- ── Seção 1: Estratégia de bônus ─────────────────────── -->
          <div class="section-box">
            <div class="section-box-title">
              <feather-icon icon="AwardIcon" size="14" class="mr-50" />
              Estratégia de bônus
            </div>

      <div class="mb-2">
        <label class="form-label font-weight-bolder">
          Bônus desejado
          <feather-icon
            id="bonus-info"
            icon="InfoIcon"
            size="14"
            class="text-muted ml-25"
          />
          <b-tooltip target="bonus-info">
            Atingindo a meta de EPs, você recebe o bônus sobre o faturamento de EP do trimestre.
          </b-tooltip>
        </label>
        <div class="d-flex flex-wrap" style="gap: 0.5rem">
          <div
            v-for="tier in bonusTiers"
            :key="tier.ep_target"
            class="bonus-tier-card"
            :class="{ active: form.bonus_target === tier.ep_target }"
            @click="selectBonusTier(tier)"
          >
            <div class="font-weight-bolder">{{ tier.ep_target }} EPs</div>
            <div class="text-muted font-small-2">{{ tier.bonus_percentage }}% bônus</div>
          </div>
        </div>
        <small v-if="$v.form.bonus_target.$error" class="text-danger">Selecione um bônus</small>
      </div>

      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Meta de EP" label-class="font-weight-bolder">
            <b-form-input
              v-model.number="form.ep_quantity"
              type="number"
              :min="form.bonus_target || 1"
              placeholder="Quantidade de EPs"
              :state="$v.form.ep_quantity.$dirty ? !$v.form.ep_quantity.$error : null"
              @input="recalculate"
            />
            <small v-if="$v.form.ep_quantity.$error" class="text-danger">
              Informe uma quantidade de EP maior ou igual ao bônus selecionado.
            </small>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Ticket médio de EP" label-class="font-weight-bolder">
            <cleave
              v-model="form.ep_avg_ticket"
              class="form-control"
              :options="moneyOptions"
              placeholder="R$ 0,00"
              @input="recalculate"
            />
            <small v-if="$v.form.ep_avg_ticket.$error" class="text-danger">Informe o ticket médio de EP.</small>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Derived: EP revenue + pro-labore -->
      <div class="derived-block">
        <div class="derived-row">
          <span class="text-muted font-small-3">Meta de faturamento EP</span>
          <span class="font-weight-bolder">{{ epRevenue | toCurrency }}</span>
        </div>
        <div class="derived-row">
          <span class="text-muted font-small-3">
            Pró-labore EP ({{ (commissionRates.ep_percentage * 100).toFixed(0) }}% — sem bônus)
          </span>
          <span class="font-weight-bolder">{{ epProlabore | toCurrency }}</span>
        </div>
        <div v-if="epRevenue > 0" class="font-small-2 text-muted mt-25">
          Este valor atende sua expectativa? Ajuste o número de EPs ou ticket médio se precisar.
        </div>
      </div>
          </div>

      <!-- ── Seção 2: Eficiência do funil ──────────────────────── -->
      <div class="section-box">
        <div class="section-box-title">
          <feather-icon icon="FilterIcon" size="14" class="mr-50" />
          Eficiência do funil
        </div>

      <b-form-group label-class="font-weight-bolder">
        <template #label>
          Eficiência EA→EF
          <span class="text-muted font-small-3 ml-25">(quantos EAs viram um EF)</span>
        </template>
        <div class="slider-row">
          <b-form-input
            v-model.number="form.ea_ef_percentage"
            type="range"
            min="1"
            max="100"
            class="flex-grow-1"
            @input="recalculate"
          />
          <b-form-input
            v-model.number="form.ea_ef_percentage"
            type="number"
            min="1"
            max="100"
            class="slider-number"
            @input="recalculate"
            @blur="clampPct('ea_ef_percentage')"
          />
          <span class="slider-suffix">%</span>
        </div>
      </b-form-group>

      <b-form-group label-class="font-weight-bolder">
        <template #label>
          Eficiência EF→EP
          <span class="text-muted font-small-3 ml-25">(quantos EFs viram um EP)</span>
        </template>
        <div class="slider-row">
          <b-form-input
            v-model.number="form.ef_ep_percentage"
            type="range"
            min="1"
            max="100"
            class="flex-grow-1"
            @input="recalculate"
          />
          <b-form-input
            v-model.number="form.ef_ep_percentage"
            type="number"
            min="1"
            max="100"
            class="slider-number"
            @input="recalculate"
            @blur="clampPct('ef_ep_percentage')"
          />
          <span class="slider-suffix">%</span>
        </div>
      </b-form-group>

      <div v-if="calculated" class="derived-block">
        <div class="derived-row" v-for="item in funnelDerived" :key="item.label">
          <span class="text-muted font-small-3">{{ item.label }}</span>
          <span class="font-weight-bolder">{{ item.value }}</span>
        </div>
      </div>
      </div>
          </b-col>

          <b-col cols="12" lg="6">
      <!-- ── Seção 3: Geração de demanda ───────────────────────── -->
      <div class="section-box">
        <div class="section-box-title">
          <feather-icon icon="UsersIcon" size="14" class="mr-50" />
          Geração de demanda
        </div>

        <b-row>
          <b-col cols="12" md="4">
            <b-form-group label="Leads por EA" label-class="font-weight-bolder">
              <b-form-input
                v-model.number="form.ea_leads_quantity"
                type="number"
                min="0"
                placeholder="Sugestão: 5"
                @input="recalculate"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="Leads quentes por EA" label-class="font-weight-bolder">
              <b-form-input
                v-model.number="form.ea_hot_leads_quantity"
                type="number"
                min="0"
                placeholder="Sugestão: 3"
                @input="recalculate"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="Recomendações por EF" label-class="font-weight-bolder">
              <b-form-input
                v-model.number="form.recommendations_per_ef"
                type="number"
                min="0"
                placeholder="Sugestão: 10"
                @input="recalculate"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <!-- ── Seção 4: Produtos adicionais ──────────────────────── -->
      <div class="section-box">
        <div class="section-box-title">
          <feather-icon icon="PackageIcon" size="14" class="mr-50" />
          Produtos adicionais
        </div>

        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Eficiência de assinatura (%)" label-class="font-weight-bolder">
              <b-form-input
                v-model.number="form.subscription_efficiency_pct"
                type="number"
                min="0"
                max="100"
                placeholder="Sugestão: 100%"
                @input="recalculate"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Pacote de assinatura" label-class="font-weight-bolder">
              <v-select
                v-model="form.subscription_package_id"
                :options="subscriptionPackages"
                :reduce="pkg => pkg.id"
                label="name"
                placeholder="Selecione o pacote"
                @input="onPackageSelected"
              >
                <template #option="pkg">
                  <div class="d-flex justify-content-between align-items-center w-100">
                    <span>{{ pkg.name }}</span>
                    <span v-if="pkg.default_ticket && !pkg.min_ticket" class="text-muted font-small-2 ml-1">
                      {{ pkg.default_ticket | toCurrency }}
                    </span>
                    <span v-else-if="pkg.min_ticket" class="text-muted font-small-2 ml-1">
                      a partir de {{ pkg.min_ticket | toCurrency }}
                    </span>
                  </div>
                </template>
              </v-select>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Ticket médio de assinatura" label-class="font-weight-bolder">
              <cleave
                v-model="form.subscription_avg_ticket"
                class="form-control"
                :options="moneyOptions"
                placeholder="R$ 0,00"
                @input="recalculate"
              />
              <small v-if="isPremiumSelected" class="text-muted d-block">
                Acompanhamento Premium: valor personalizado por entrevista, mínimo
                {{ premiumMinTicket | toCurrency }}.
              </small>
              <small v-if="premiumTicketBelowMin" class="text-danger">
                O ticket do Acompanhamento Premium deve ser de no mínimo {{ premiumMinTicket | toCurrency }}.
              </small>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Ticket médio de soluções" label-class="font-weight-bolder">
              <cleave
                v-model="form.solutions_avg_ticket"
                class="form-control"
                :options="moneyOptions"
                placeholder="R$ 0,00"
                @input="recalculate"
              />
              <div class="d-flex flex-wrap mt-50" style="gap: 0.35rem">
                <b-button
                  v-for="pct in solutionsPctOptions"
                  :key="pct"
                  size="sm"
                  variant="outline-secondary"
                  :title="solutionsTicketFor(pct) | toCurrency"
                  @click="applySolutionsPct(pct)"
                >
                  {{ pct }}%
                </b-button>
              </div>
              <small class="text-muted">Percentual aplicado sobre o ticket médio de EP.</small>
            </b-form-group>
          </b-col>
        </b-row>
      </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- ── Card 3: Resumo do OKR ─────────────────────────────── -->
      <b-card>
      <div v-if="calculated" class="okr-total-summary mb-2">
        <div class="summary-header">
          <feather-icon icon="ClipboardIcon" size="15" class="mr-50" />
          <span class="font-weight-bolder">Resumo do OKR</span>
        </div>

        <div class="summary-section-title">Funil comercial</div>
        <div class="summary-chips">
          <div v-for="chip in funnelChips" :key="chip.label" class="summary-chip">
            <div class="chip-value">{{ chip.value }}</div>
            <div class="chip-label">{{ chip.label }}</div>
          </div>
        </div>

        <div class="summary-section-title">Faturamento</div>
        <div class="derived-row">
          <span class="text-muted font-small-3">Entrevistas Pagas</span>
          <span class="font-weight-bolder">{{ calculated.ep_revenue | toCurrency }}</span>
        </div>
        <div class="derived-row">
          <span class="text-muted font-small-3">
            Assinaturas — {{ calculated.subscriptions_qty }} no tri ({{ calculated.subscriptions_per_month }}/mês)
          </span>
          <span class="font-weight-bolder">{{ calculated.subscription_revenue | toCurrency }}</span>
        </div>
        <div class="derived-row">
          <span class="text-muted font-small-3">Soluções financeiras</span>
          <span class="font-weight-bolder">{{ calculated.solutions_revenue | toCurrency }}</span>
        </div>
        <div class="derived-row summary-total">
          <span class="font-small-3 font-weight-bolder">Faturamento total trimestral</span>
          <span class="font-weight-bolder text-primary">{{ calculated.total_revenue | toCurrency }}</span>
        </div>

        <div class="summary-section-title">Pró-labore</div>
        <div class="derived-row">
          <span class="text-muted font-small-3">Entrevistas Pagas</span>
          <span class="font-weight-bolder">{{ calculated.ep_prolabore | toCurrency }}</span>
        </div>
        <div class="derived-row">
          <span class="text-muted font-small-3">Assinaturas</span>
          <span class="font-weight-bolder">{{ calculated.sub_prolabore | toCurrency }}</span>
        </div>
        <div class="derived-row">
          <span class="text-muted font-small-3">Soluções financeiras</span>
          <span class="font-weight-bolder">{{ calculated.sol_prolabore | toCurrency }}</span>
        </div>
        <div class="derived-row summary-total">
          <span class="font-small-3 font-weight-bolder">Pró-labore total trimestral</span>
          <span class="font-weight-bolder text-primary">{{ calculated.total_prolabore | toCurrency }}</span>
        </div>
        <div class="derived-row">
          <span class="text-muted font-small-3">Pró-labore mensal (÷ 3)</span>
          <span class="font-weight-bolder">{{ calculated.monthly_prolabore | toCurrency }}</span>
        </div>

        <template v-if="calculated.bonus_value > 0">
          <div class="summary-section-title">Bônus</div>
          <div class="derived-row">
            <span class="text-muted font-small-3">Bônus de EP (ao atingir a meta)</span>
            <span class="font-weight-bolder text-success">{{ calculated.bonus_value | toCurrency }}</span>
          </div>
        </template>
      </div>

      <div v-else class="text-muted font-small-3 mb-2">
        Preencha os campos acima para visualizar o resumo do OKR.
      </div>

      <b-button
        block
        variant="primary"
        :disabled="saving"
        @click="save"
      >
        <b-spinner v-if="saving" small class="mr-25" />
        Salvar OKR
      </b-button>
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BCard, BButton, BFormGroup, BFormInput, BSpinner, BTooltip, BRow, BCol, BAlert,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import Cleave from 'vue-cleave-component'
import vSelect from 'vue-select'
import OkrPremisesBlock from './OkrPremisesBlock.vue'
import { required, minValue } from 'vuelidate/lib/validators'
import { mapActions } from 'vuex'
import * as types from '../store/types'

const SOLUTIONS_PCT_OPTIONS = [25, 50, 75, 100]

export default {
  name: 'OkrFormNew',
  components: {
    BCard, BButton, BFormGroup, BFormInput, BSpinner, BTooltip, BRow, BCol, BAlert,
    FeatherIcon, Cleave, vSelect, OkrPremisesBlock,
  },

  props: {
    consultantId: { type: Number, required: true },
    year:         { type: Number, required: true },
    quarter:      { type: Number, required: true },
  },

  data() {
    return {
      loading:         true,
      loadError:       false,
      saving:          false,
      hasExisting:     false,
      isLegacyOkr:     false,
      bonusTiers:      [],
      subscriptionPackages: [],
      solutionsPctOptions:  SOLUTIONS_PCT_OPTIONS,
      commissionRates: { ep_percentage: 0, subscription_percentage: 0, solutions_percentage: 0 },
      calculated:      null,
      form: {
        bonus_target:                null,
        ep_quantity:                 null,
        ep_avg_ticket:               null,
        ef_ep_percentage:            50,
        ea_ef_percentage:            33,
        ea_leads_quantity:           5,
        ea_hot_leads_quantity:       3,
        recommendations_per_ef:      10,
        subscription_efficiency_pct: 100,
        subscription_package_id:     null,
        subscription_avg_ticket:     null,
        solutions_avg_ticket:        null,
      },
      moneyOptions: {
        numeral:           true,
        numeralDecimalMark: ',',
        delimiter:          '.',
        prefix:             'R$ ',
        rawValueTrimPrefix: true,
      },
    }
  },

  validations() {
    return {
      form: {
        bonus_target: { required },
        ep_quantity: {
          required,
          minValue: minValue(this.form.bonus_target || 1),
        },
        ep_avg_ticket: { required, minValue: minValue(0.01) },
      },
    }
  },

  computed: {
    quarterLabel() {
      return `${this.quarter}º trimestre de ${this.year}`
    },
    quarterMonthsLabel() {
      const months = {
        1: 'Janeiro a março',
        2: 'Abril a junho',
        3: 'Julho a setembro',
        4: 'Outubro a dezembro',
      }
      return `${months[this.quarter] || ''} de ${this.year}`
    },
    epRevenue() {
      return (this.form.ep_quantity || 0) * (this.moneyToFloat(this.form.ep_avg_ticket) || 0)
    },
    epProlabore() {
      return this.epRevenue * (this.commissionRates.ep_percentage || 0)
    },
    funnelDerived() {
      if (!this.calculated) return []
      return [
        { label: 'EF necessário',  value: this.calculated.ef_quantity },
        { label: 'EA necessário',  value: this.calculated.ea_quantity },
      ]
    },
    funnelChips() {
      if (!this.calculated) return []
      return [
        { label: 'Leads',         value: this.calculated.leads_quantity },
        { label: 'Leads quentes', value: this.calculated.hot_leads_quantity },
        { label: 'EA',            value: this.calculated.ea_quantity },
        { label: 'EF',            value: this.calculated.ef_quantity },
        { label: 'EP',            value: this.calculated.ep_quantity },
        { label: 'Recomendações', value: this.calculated.recommendations_qty },
        { label: 'MXA',           value: this.calculated.mxa_quantity },
        { label: 'PA',            value: this.calculated.pa_quantity },
      ]
    },
    selectedPackage() {
      return this.subscriptionPackages.find(p => p.id === this.form.subscription_package_id) || null
    },
    isPremiumSelected() {
      return !!this.selectedPackage?.min_ticket
    },
    premiumMinTicket() {
      return this.selectedPackage?.min_ticket || 0
    },
    premiumTicketBelowMin() {
      if (!this.isPremiumSelected) return false
      const ticket = this.moneyToFloat(this.form.subscription_avg_ticket) || 0
      return ticket > 0 && ticket < this.premiumMinTicket
    },
  },

  watch: {
    consultantId: 'init',
    year:         'init',
    quarter:      'init',
  },

  mounted() {
    this.init()
  },

  methods: {
    ...mapActions({
      fetchBonusTiers:           types.GET_BONUS_TIERS,
      fetchCommissionRates:      types.GET_CONSULTANT_COMMISSION_RATES,
      fetchSubscriptionPackages: types.GET_SUBSCRIPTION_PACKAGES,
      savePlannedOkr:            types.SAVE_PLANNED_OKR,
      getExistingOkr:            types.GET_PLANNED_OKR,
    }),

    async init() {
      this.loading   = true
      this.loadError = false
      try {
        const [tiersResp, ratesResp, packagesResp] = await Promise.all([
          this.fetchBonusTiers(this.consultantId),
          this.fetchCommissionRates(this.consultantId),
          this.fetchSubscriptionPackages(),
        ])
        this.bonusTiers           = tiersResp.data.tiers
        this.commissionRates      = ratesResp.data
        this.subscriptionPackages = packagesResp.data.packages || []

        // Pre-select first (and possibly only) tier
        if (this.bonusTiers.length === 1) {
          this.selectBonusTier(this.bonusTiers[0])
        }

        // Load existing OKR if present
        await this.loadExistingOkr()
      } catch (err) {
        console.error('[OkrFormNew] init failed:', err)
        this.loadError = true
      } finally {
        this.loading = false
      }
    },

    async loadExistingOkr() {
      this.hasExisting = false
      this.isLegacyOkr = false
      try {
        const resp = await this.getExistingOkr({
          year: this.year, quarter: this.quarter, consultant_id: this.consultantId,
        })
        const okr = resp.data
        if (okr && !okr.ep_quantity && (okr.okr_total || okr.ep_consultant_commission_amount)) {
          // registro do formulário antigo: sem os campos do modelo novo
          this.hasExisting = true
          this.isLegacyOkr = true
        }
        if (okr && okr.ep_quantity) {
          this.hasExisting                      = true
          this.form.bonus_target                = okr.bonus_target
          this.form.ep_quantity                 = okr.ep_quantity
          this.form.ep_avg_ticket               = this.toMoneyRaw(okr.ep_avg_ticket)
          this.form.ef_ep_percentage            = okr.ef_ep_percentage
          this.form.ea_ef_percentage            = okr.ea_ef_percentage
          this.form.ea_leads_quantity           = okr.ea_leads_quantity
          this.form.ea_hot_leads_quantity       = okr.ea_hot_leads_quantity
          this.form.recommendations_per_ef      = okr.recommendations_per_ef ?? 10
          this.form.subscription_efficiency_pct = okr.subscription_efficiency_pct ?? 100
          this.form.subscription_package_id     = okr.subscription_package_id ?? null
          this.form.subscription_avg_ticket     = this.toMoneyRaw(okr.subscription_avg_ticket)
          this.form.solutions_avg_ticket        = this.toMoneyRaw(okr.solutions_avg_ticket)
          this.recalculate()
        }
      } catch { /* no existing OKR */ }
    },

    // Cleave (numeralDecimalMark: ',') só formata corretamente valores
    // programáticos em vírgula decimal — um número/string em ponto (ex.: o
    // "199.9" que vem do backend) é lido como "1999" na primeira renderização
    // do campo, porque o ponto é tratado como separador de milhar, não como
    // decimal. Por isso todo valor carregado (não digitado pelo usuário)
    // precisa passar por aqui antes de ir para o form.
    toMoneyRaw(value) {
      const num = parseFloat(value)
      if (Number.isNaN(num)) return null
      return num.toFixed(2).replace('.', ',')
    },

    // parseFloat nativo não entende vírgula decimal — usar sempre que for ler
    // um campo mascarado (Cleave pode devolver vírgula ou ponto dependendo
    // se o valor veio de digitação ou foi setado programaticamente).
    moneyToFloat(value) {
      if (value === null || value === undefined || value === '') return 0
      return parseFloat(String(value).replace(',', '.')) || 0
    },

    applyPremises(p) {
      if (p.ep_avg_ticket)    this.form.ep_avg_ticket    = this.toMoneyRaw(p.ep_avg_ticket)
      if (p.ea_ef_efficiency) this.form.ea_ef_percentage = p.ea_ef_efficiency
      if (p.ef_ep_efficiency) this.form.ef_ep_percentage = p.ef_ep_efficiency
      this.recalculate()
    },

    onPackageSelected() {
      const pkg = this.selectedPackage
      if (pkg && pkg.default_ticket) {
        this.form.subscription_avg_ticket = this.toMoneyRaw(pkg.default_ticket)
      }
      this.recalculate()
    },

    clampPct(field) {
      const value = Number(this.form[field]) || 1
      this.form[field] = Math.min(100, Math.max(1, Math.round(value)))
      this.recalculate()
    },

    solutionsTicketFor(pct) {
      return (this.moneyToFloat(this.form.ep_avg_ticket) || 0) * (pct / 100)
    },
    applySolutionsPct(pct) {
      this.form.solutions_avg_ticket = this.solutionsTicketFor(pct).toFixed(2)
      this.recalculate()
    },

    selectBonusTier(tier) {
      this.form.bonus_target = tier.ep_target
      this.form.ep_quantity = tier.ep_target
      this.recalculate()
    },

    recalculate() {
      const ep        = this.form.ep_quantity || 0
      const epTicket  = this.moneyToFloat(this.form.ep_avg_ticket) || 0
      const efEpPct   = this.form.ef_ep_percentage || 0
      const eaEfPct   = this.form.ea_ef_percentage || 0
      const leadsPerEa  = this.form.ea_leads_quantity || 5
      const hotPerEa    = this.form.ea_hot_leads_quantity || 3
      const recsPerEf   = this.form.recommendations_per_ef || 10
      const subEffPct   = this.form.subscription_efficiency_pct || 100
      const subTicket   = this.moneyToFloat(this.form.subscription_avg_ticket) || 0
      const solTicket   = this.moneyToFloat(this.form.solutions_avg_ticket) || 0
      const epComm      = this.commissionRates.ep_percentage || 0
      const subComm     = this.commissionRates.subscription_percentage || 0
      const solComm     = this.commissionRates.solutions_percentage || 0
      const bonusTarget = this.form.bonus_target || 0
      const bonusTier   = this.bonusTiers.find(t => t.ep_target === bonusTarget)
      const bonusPct    = bonusTier?.bonus_percentage || 0

      const ef       = efEpPct > 0 ? Math.ceil(ep / (efEpPct / 100)) : 0
      const ea       = eaEfPct > 0 ? Math.ceil(ef / (eaEfPct / 100)) : 0
      const leads    = Math.ceil(ea * leadsPerEa)
      const hotLeads = Math.ceil(ea * hotPerEa)
      const recs     = Math.ceil(ef * recsPerEf)

      const epRevenue  = ep * epTicket
      // recorrência de assinatura — espelho do OkrCalculationService
      const subsPerMonth = Math.ceil((ep / 3) * (subEffPct / 100))
      const subQty       = subsPerMonth * 3
      const subRevenue   = subsPerMonth * subTicket * 6
      const solRevenue = ep * solTicket
      const totalRev   = epRevenue + subRevenue + solRevenue

      const epProlabore  = epRevenue  * epComm
      const subProlabore = subRevenue * subComm
      const solProlabore = solRevenue * solComm
      const totalProlabore   = epProlabore + subProlabore + solProlabore
      const monthlyProlabore = totalProlabore / 3

      const bonusValue = bonusTarget > 0 && ep >= bonusTarget
        ? epRevenue * (bonusPct / 100)
        : 0

      this.calculated = {
        ef_quantity: ef, ea_quantity: ea,
        leads_quantity: leads, hot_leads_quantity: hotLeads,
        recommendations_qty: recs, ep_quantity: ep,
        ep_revenue: epRevenue, subscriptions_qty: subQty,
        subscriptions_per_month: subsPerMonth,
        subscription_revenue: subRevenue, mxa_quantity: ep,
        solutions_revenue: solRevenue, pa_quantity: ep * 2,
        total_revenue: totalRev,
        ep_prolabore: epProlabore, sub_prolabore: subProlabore,
        sol_prolabore: solProlabore, total_prolabore: totalProlabore,
        monthly_prolabore: monthlyProlabore, bonus_value: bonusValue,
      }
    },

    async save() {
      this.$v.$touch()
      if (this.$v.$error) return
      // mínimo do Premium também é validado no backend
      if (this.isPremiumSelected
        && (this.moneyToFloat(this.form.subscription_avg_ticket) || 0) < this.premiumMinTicket) {
        return
      }

      this.saving = true
      try {
        await this.savePlannedOkr({
          year:                        this.year,
          quarter:                     this.quarter,
          consultant_id:               this.consultantId,
          bonus_target:                this.form.bonus_target,
          ep_quantity:                 this.form.ep_quantity,
          ep_avg_ticket:               this.moneyToFloat(this.form.ep_avg_ticket) || 0,
          ef_ep_percentage:            this.form.ef_ep_percentage,
          ea_ef_percentage:            this.form.ea_ef_percentage,
          ea_leads_quantity:           this.form.ea_leads_quantity,
          ea_hot_leads_quantity:       this.form.ea_hot_leads_quantity,
          recommendations_per_ef:      this.form.recommendations_per_ef,
          subscription_efficiency_pct: this.form.subscription_efficiency_pct,
          subscription_package_id:     this.form.subscription_package_id,
          subscription_avg_ticket:     this.moneyToFloat(this.form.subscription_avg_ticket) || 0,
          solutions_avg_ticket:        this.moneyToFloat(this.form.solutions_avg_ticket) || 0,
        })
        this.$emit('saved')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
/* trimestre visível durante a rolagem */
.okr-form-header {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #f8f8f8;
  padding: 0.5rem 0.25rem;
  margin-bottom: 1rem;
}
.dark-layout .okr-form-header {
  background: #161d31;
}

.card-block-title {
  font-weight: 700;
  font-size: 1.05rem;
}

::v-deep .form-group {
  margin-bottom: 1.4rem;
}
.bonus-tier-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 0.6rem 1rem;
  cursor: pointer;
  min-width: 110px;
  text-align: center;
  transition: border-color 0.15s, background 0.15s;
}
.bonus-tier-card.active {
  border-color: var(--primary);
  background: rgba(var(--primary-rgb), 0.08);
}
.derived-block {
  background: #f8f8f8;
  border-radius: 6px;
  padding: 0.6rem 0.75rem;
}
.derived-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.15rem 0;
}
.okr-total-summary {
  background: #f0f4ff;
  border-radius: 6px;
  padding: 0.85rem;
}
.summary-header {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
}
.summary-section-title {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6e6b7b;
  margin: 0.75rem 0 0.35rem;
}
.summary-chips {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(86px, 1fr));
  gap: 0.4rem;
}
.summary-chip {
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  padding: 0.35rem 0.4rem;
  text-align: center;
}
.chip-value {
  font-weight: 700;
  line-height: 1.2;
}
.chip-label {
  font-size: 0.66rem;
  color: #6e6b7b;
  white-space: nowrap;
}
.summary-total {
  border-top: 1px dashed #d0d2d6;
  margin-top: 0.3rem;
  padding-top: 0.4rem;
}
.dark-layout .summary-chip {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
}
.dark-layout .summary-total {
  border-top-color: #3b4253;
}
.block-divider {
  border-top: 1px solid #dee2e6;
}
.section-box {
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1.25rem;
}
.section-box-title {
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--primary);
  margin-bottom: 1.25rem;
}
.dark-layout .section-box {
  border-color: #3b4253;
}
.slider-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.slider-number {
  width: 68px;
  flex: 0 0 68px;
  text-align: right;
  padding: 0.25rem 0.5rem;
}
.slider-suffix {
  font-weight: 600;
  font-size: 0.85rem;
}
.dark-layout .derived-block {
  background: rgba(255, 255, 255, 0.04);
}
.dark-layout .okr-total-summary {
  background: rgba(0, 82, 204, 0.1);
}
</style>
